
import React from 'react';
import { UseFormReturn } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { 
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import { MedewerkerResponse } from "@/integrations/supabase/medewerkerTypes";
import { CampaignFormValues } from "./types/formTypes";

interface UserSelectionProps {
  form: UseFormReturn<CampaignFormValues>;
}

const UserSelection: React.FC<UserSelectionProps> = ({ form }) => {
  // Fetch all users for the dropdown
  const { data: users = [] } = useQuery({
    queryKey: ["medewerkers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("medewerkers")
        .select("id, naam, username")
        .eq("active", true);
      
      if (error) throw error;
      return data as MedewerkerResponse[];
    }
  });
  
  return (
    <FormField
      control={form.control}
      name="users"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Gebruikers</FormLabel>
          <Select 
            onValueChange={(value) => {
              // Handle special case for "all"
              if (value === "all") {
                const allUserIds = users.map(user => user.id);
                field.onChange(allUserIds);
              } else {
                const currentUsers = field.value || [];
                // Toggle user selection
                const newUsers = currentUsers.includes(value)
                  ? currentUsers.filter(id => id !== value)
                  : [...currentUsers, value];
                field.onChange(newUsers);
              }
            }}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer gebruikers">
                  {field.value?.length || 0} gebruikers geselecteerd
                </SelectValue>
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="all">Alle gebruikers selecteren</SelectItem>
              {users.map((user) => (
                <div key={user.id} className="flex items-center p-2">
                  <input
                    type="checkbox"
                    id={`user-${user.id}`}
                    checked={field.value?.includes(user.id)}
                    onChange={() => {
                      const currentUsers = field.value || [];
                      const newUsers = currentUsers.includes(user.id)
                        ? currentUsers.filter(id => id !== user.id)
                        : [...currentUsers, user.id];
                      field.onChange(newUsers);
                    }}
                    className="mr-2"
                  />
                  <label htmlFor={`user-${user.id}`}>
                    {user.naam} ({user.username})
                  </label>
                </div>
              ))}
            </SelectContent>
          </Select>
          <FormDescription>
            Voor welke gebruikers is deze modal bedoeld?
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default UserSelection;
